import { WidgetSize } from 'shared/constants/widgets'

export type ArtifactKind = 'widget' | 'dashboard' | 'presentation'

// Sharing gate: draft = private (never shared); pending = awaiting reviewer;
// approved = visible to recipients; declined = rejected (with a reason).
export type ArtifactReviewStatus = 'draft' | 'pending' | 'approved' | 'declined'

type AiReviewVerdict = 'pass' | 'flag' | 'unknown'

// Which share actions require review (mirrors the forge env). Frames the share
// dialog: 'any' = per-user + org gated, 'org' = only org gated, 'off' = none.
export type ArtifactReviewScope = 'any' | 'org' | 'off'

// One finding from the advisory AI/static security review of a widget's HTML.
interface ArtifactAiFinding {
  severity?: string
  type?: string
  evidence?: string
  explanation?: string
}

// Result of the on-demand "Let AI review it" action.
export interface AiReviewResult {
  verdict: AiReviewVerdict
  summary?: string
  findings?: ArtifactAiFinding[]
  external_hosts?: string[]
  llm_ran?: boolean
}

// Known keys inside the `properties` metadata bag. `Record<string, unknown>`
// is still the index signature so unknown server-side additions don't break
// compilation, but the well-known keys are typed so callers stop papering
// over with `as unknown` + `Array.isArray` guards.
interface ArtifactProperties {
  // hosts the backend auto-detected at save time (immutable from the client)
  detected_hosts?: string[]
  // hosts a reviewer has confirmed the widget may reach (CSP allowlist)
  allowed_hosts?: string[]
}

export interface ArtifactSummary {
  public_id: string
  // Optional for legacy cached responses; Forge returns string | null.
  category_id?: string | null
  kind?: ArtifactKind
  title: string
  description?: string | null
  default_size: WidgetSize
  // Extensible metadata bag (default_size, kind, owner info, detected_hosts /
  // allowed_hosts, + anything stored).
  properties?: Record<string, unknown> & ArtifactProperties
  thread_id?: string | null
  created_at?: string
  updated_at?: string
  mine: boolean
  // May refine/share/delete it: the owner, or an org admin/reviewer (forge).
  can_edit?: boolean
  // Creator, surfaced for every artifact so tiles can show "by <name>".
  owner_id?: string | null
  owner_name?: string | null
  shared_by_id?: string | null
  shared_by_name?: string | null
  // Review/approval state (present once the sharing gate is live).
  review_status?: ArtifactReviewStatus
  review_reason?: string | null
  reviewed_by_name?: string | null
  reviewed_at?: string | null
  ai_review_verdict?: AiReviewVerdict | null
  ai_review_findings?: ArtifactAiFinding[] | null
}

export interface ArtifactUser {
  id: string
  name: string
  email?: string
  avatar?: string
}

export interface ArtifactShare {
  user_id: string
  permission: string
  name?: string
  avatar?: string
  email?: string
}

export interface ArtifactOrgShare {
  org_id: string
  name?: string
}

export interface ArtifactPositionShare {
  position_id: string
  name?: string
  department_id?: number
  created_at?: string
}

export interface ArtifactCategory {
  id: string
  name: string
  scope: string
}
